import { Canvas } from "fabric/*";
import React, { useEffect, useState } from "react";
import { CloseButton } from "react-bootstrap";
import { ModalType, useModal } from "../../Context/ModalContext";
import { useAppDispatch, useAppSelector } from "../../hook/redux";
import { makeXhrRequest } from "../../utils/makeXhrRequest";
import { decodeHtmlEntities } from "./ModalUtils";


interface TextPropertiesSelectorModalProps {
  canvas: Canvas;
  option?: string;
}

function TextGeneratorModal( { canvas }: TextPropertiesSelectorModalProps ) {
  const { textGeneratorReqParams } = useAppSelector((state) => state.editor);

  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { show, toggleShow, type } = useModal();

  const dispatch = useAppDispatch();

  /**
   * Fetches all elements based on the provided `id` and `type` parameters and updates the application state with the
   * response.
   *
   * This function sends an asynchronous HTTP POST request to fetch data from the server and processes the response to
   * update the corresponding state variables. It handles different response types and errors, ensuring proper state
   * updates for loading, errors, and fetched elements.
   *
   * @param {string} id - The identifier used to fetch specific elements.
   * @param {string} type - The type of the elements being requested.
   */
  const fetchAllElements = (id: string, type: string) => {
    const newReqParams = {
      id: id,
      type: type,
      parentId:
        textGeneratorReqParams.length > 0
        ? textGeneratorReqParams[textGeneratorReqParams.length - 1].id
        : "",
    };

    if (newReqParams.id !== newReqParams.parentId) {
      dispatch({
        type: "editor/updateTextGeneratorReqParams",
        payload: [...textGeneratorReqParams, newReqParams],
      });
    }

    setLoading(true);
    setError(null);

    const url = `${process.env.PREFIX_URL}/v2017/ajax/ajax.php?traitement=load-model-text-card`;
    const method = "POST";
    const headers = {
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Requested-With": "XMLHttpRequest",
    };
    const body = `id=${encodeURIComponent(id)}&outpout=json&type=${encodeURIComponent(type)}`;

    makeXhrRequest(
      url,
      method,
      headers,
      body,
      (data) => {
        setLoading(false);
        setElements(data);
      },
      (errorMessage) => {
        setLoading(false);
        console.error(errorMessage);
        setError(errorMessage);
      },
      () => setLoading(false)
    );
  };
  

  /**
   * A function that handles the "back" action by navigating to the parent request
   * in the `textGeneratorReqParams` array. It fetches all elements related to the
   * parent request and updates the state to reflect the change.
   *
   * The "back" operation identifies the parent request of the current request
   * and dispatches an update to maintain the history up to that parent request.
   *
   */
  const handleBack = () => {
    const currentReq =
      textGeneratorReqParams[textGeneratorReqParams.length - 1];
    const parentReq = textGeneratorReqParams.find(
      (param) => param.id === currentReq.parentId
    );

    if (parentReq) {
      fetchAllElements(parentReq.id, parentReq.type);
      const parentIndex = textGeneratorReqParams.findIndex(
        (param) => param.id === parentReq.id
      );
      dispatch({
        type: "editor/updateTextGeneratorReqParams",
        payload: textGeneratorReqParams.slice(0, parentIndex + 1),
      });
    }
  };

  /**
   * Handles the text editing on a canvas by updating the text content of a
   * textbox object. The function searches for an object of type "textbox"
   * on the canvas, updates its text content with the given value after decoding
   * HTML entities, and re-renders the canvas. Also toggles the visibility
   * of a modal related to text generation.
   *
   * @param {string} value - The new text string to set in the textbox object.
   */
  const handleTxtEdit = (value: string) => {
    const textObject = canvas._objects.find((obj) => obj.type === "textbox");
    if (textObject) {
      textObject.set({ text: decodeHtmlEntities(value) });
      canvas.renderAll();
      toggleShow(false, null, ModalType.textGenerator)
    }
  }

  /**
   * Handles the click event for AI text elements.
   *
   */
  const handleAiTextClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      toggleShow(false, null, type);
    }
  };

  useEffect(() => {
    if (show && type === ModalType.textGenerator) {
      const currentReq =
        textGeneratorReqParams[textGeneratorReqParams.length - 1];
      currentReq
        ? fetchAllElements(currentReq.id, currentReq.type)
        : fetchAllElements("0", "rub");
    }
  }, [show, type]);

  return (
    <div
      className={`custom-modal-background ${show && type === ModalType.textGenerator ? "" : "hidden"}`}
      onClick={handleAiTextClick}
    >
      <div className="custom-modal-content bg-white">
        <div className="custom-modal-header">
          <CloseButton
            onClick={() => toggleShow(false, null, type)}
            aria-label="Close"
          />
        </div>
        <div>
          <div className="custom-modal-title text-muted">
            Générer un texte sur-mesure
          </div>
          <div className="custom-modal-grid py-3">
            <div
              className="d-flex justify-content-between align-items-center p-3 w-100"
              style={{
                borderRadius: "20px",
                cursor: "pointer",
                backgroundColor: "#e8e3f9",
                fontSize: "18px",
                fontFamily: "'Work Sans', sans-serif",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#dcd6f2")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#e8e3f9")
              }
              onClick={() => {
                toggleShow(false, null, type);
                toggleShow(true, null, ModalType.aItextGenerator);
              }}
            >
              <p className="m-0 d-inline-flex gap-2 align-items-center">
                <i className="fa fa-wand-magic-sparkles" aria-hidden="true"></i>
                Assistant intelligent à la rédaction
              </p>
              <i
                className="fa fa-arrow-circle-right"
                aria-hidden="true"
                style={{ color: "#7d59ff", fontSize: "22px" }}
              ></i>
            </div>
          </div>
          <div className="custom-modal-title text-muted">
            Choisir un texte existant
          </div>
          {textGeneratorReqParams.length > 1 && (
            <div className="d-flex">
              <div
                className="btn btn-lg custom-button-outline"
                onClick={handleBack}
              >
                <i
                  className="fa fa-arrow-circle-left"
                  aria-hidden="true"
                  style={{ color: "#7d59ff", fontSize: "22px" }}
                ></i>
                <span>Retour</span>
              </div>
            </div>
          )}
          <div className="d-flex flex-column gap-3 py-3">
            {loading && <p>Chargement des éléments...</p>}
            {error && <p className="text-danger">Erreur : {error}</p>}
            {!loading &&
              !error &&
              elements &&
              elements.map(
                (
                  element: { id: string; type: string; value: string },
                  index
                ) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between align-items-center p-3 w-100"
                    style={{
                      borderRadius: "20px",
                      cursor: "pointer",
                      backgroundColor: "#e8e3f9",
                      fontSize: "18px",
                      fontFamily: "'Work Sans', sans-serif",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#dcd6f2")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#e8e3f9")
                    }
                    onClick={() => {
                      element.type !== "txt" ? fetchAllElements(element.id, element.type) : handleTxtEdit(element.value);
                    }}
                  >
                    {decodeHtmlEntities(element.value)}
                    <i
                      className="fa fa-arrow-circle-right"
                      aria-hidden="true"
                      style={{ color: "#7d59ff", fontSize: "22px" }}
                    ></i>
                  </div>
                )
              )}
          </div>
          <div className="d-flex justify-content-end">
            <div
              className="btn custom-button-outline"
              onClick={() => {
                toggleShow(false, null, ModalType.textGenerator);
              }}
            >
              <span>Fermer</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TextGeneratorModal;
